import { useEffect, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { useDocumentDelete, useDocumentSubmit } from '@repo/api/risk/documents';
import { riskQueries } from '@repo/api/risk/risks';
import { useUser } from '@repo/auth';
import { DocumentsPage } from '@repo/features/documents';

import { Route as DetailsRoute } from './index';

export const Route = createFileRoute('/_risks/risk/$riskId/_risk/documents')({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('risk.document.view')) {
			throw redirect({
				to: DetailsRoute.to,
				params,
			});
		}
	},
	component: function DocumentsTab() {
		const params = Route.useParams();

		const navigate = Route.useNavigate();

		const riskId = params.riskId;

		const [defaultComparisonDate] = useState(() => new Date().toISOString());

		const { permissions } = useUser();

		const { data: risk, isFetching } = useSuspenseQuery(riskQueries.id(riskId));

		useEffect(() => {
			if (!isFetching && !risk.noprCompleted) {
				void navigate({
					to: DetailsRoute.to,
				});
			}
		}, [risk.noprCompleted, isFetching, navigate]);

		const { mutateAsync: deleteDocument } = useDocumentDelete(risk.riskId);

		const { mutateAsync: submitDocument, isPending: submitting } =
			useDocumentSubmit(risk.riskId);

		return (
			<DocumentsPage
				title="Risk Documents"
				documents={risk.riskDocuments}
				stage={risk.stage}
				showFileProgress={false}
				deleteDocument={deleteDocument}
				permissions={{
					download: permissions.includes('risk.document.download'),
					close: permissions.includes('risk.close'),
				}}
				canUpload={permissions.includes('risk.document.upload')}
				countryId={risk.riskInsured.countryId}
				comparisonDate={risk.riskCreated || defaultComparisonDate}
				submitDocument={submitDocument}
				submitting={submitting}
				introAcknowledgement="submitting the risk details"
				uploadTarget="the risk"
				riskFlow
			/>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Risk', 'Documents'),
});
